import React, { useEffect, useState } from 'react';
import { scheduledReportsAPI, insightsAPI } from '../services/api';
import { formatDateBritish } from '../utils/dateUtils';

interface Client {
  id: number;
  companyName: string;
}

interface ScheduleRow {
  id: number;
  clientId: number;
  clientName: string;
  name: string;
  isEnabled: boolean;
  sendDayOfWeek: number;
  sendTimeLocal: string;
  timeZoneId: string;
  includeAdded: boolean;
  includeRemoved: boolean;
  includeTemp: boolean;
  attachExcel: boolean;
  sendIfEmpty: boolean;
  recipientCount: number;
  lastRunAt?: string | null;
  lastRunStatus?: string | null;
  lastRunError?: string | null;
  lastPeriodStart?: string | null;
  lastPeriodEnd?: string | null;
}

interface ClientUser {
  id: number;
  email: string;
  displayName: string;
  role: string;
}

interface PreviewData {
  clientName: string;
  periodLabel: string;
  addedCount: number;
  removedCount: number;
  tempCount: number;
  totalCount: number;
  subject: string;
  chronological: Array<{
    eventAtUtc: string;
    eventType: string;
    registration: string;
    site: string;
    processedBy: string;
  }>;
}

const DAY_OPTIONS = [
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
  { value: 0, label: 'Sunday' },
];

const emptyForm = {
  id: 0,
  clientId: '',
  name: 'Weekly fleet movements',
  isEnabled: true,
  sendDayOfWeek: 1,
  sendTimeLocal: '07:00',
  includeAdded: true,
  includeRemoved: true,
  includeTemp: true,
  attachExcel: true,
  sendIfEmpty: false,
  userIds: [] as number[],
  ccEmails: '',
};

const statusBadge = (status?: string | null) => {
  const s = status || '-';
  const colour =
    s === 'Success' ? '#16a34a' :
    s === 'SkippedEmpty' ? '#d97706' :
    s === 'Failed' ? '#dc2626' :
    s === 'Running' ? '#2563eb' : '#6b7280';
  return (
    <span style={{
      background: colour + '18', color: colour, border: `1px solid ${colour}55`,
      padding: '2px 8px', borderRadius: 999, fontSize: 12, fontWeight: 700,
    }}>{s}</span>
  );
};

const dayLabel = (d: number) => DAY_OPTIONS.find(x => x.value === d)?.label || String(d);

const ScheduledReportsTab: React.FC = () => {
  const [clients, setClients] = useState<Client[]>([]);
  const [schedules, setSchedules] = useState<ScheduleRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ ...emptyForm });
  const [clientUsers, setClientUsers] = useState<ClientUser[]>([]);
  const [preview, setPreview] = useState<PreviewData | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);

  useEffect(() => {
    loadAll();
  }, []);

  useEffect(() => {
    if (!form.clientId) {
      setClientUsers([]);
      return;
    }
scheduledReportsAPI.clientUsers(parseInt(form.clientId, 10))
      .then(res => {
        const raw = Array.isArray(res.data) ? res.data : [];
        setClientUsers(raw.map((u: any) => ({
          id: u.id ?? u.Id,
          email: u.email ?? u.Email ?? '',
          displayName: u.displayName ?? u.DisplayName
            ?? `${u.firstName ?? u.FirstName ?? ''} ${u.lastName ?? u.LastName ?? ''}`.trim(),
          role: u.role ?? u.Role ?? '',
        })));
      })
      .catch(() => setClientUsers([]));
  }, [form.clientId]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [cRes, sRes] = await Promise.all([
        insightsAPI.getClientsList(),
        scheduledReportsAPI.list(),
      ]);
      setClients(Array.isArray(cRes.data) ? cRes.data : []);
      setSchedules(Array.isArray(sRes.data) ? sRes.data : []);
    } catch (e) {
      console.error(e);
      alert('Failed to load scheduled reports.');
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setForm({ ...emptyForm });
    setPreview(null);
    setShowForm(true);
  };

  const openEdit = async (row: ScheduleRow) => {
    try {
      const res = await scheduledReportsAPI.get(row.id);
      const d = res.data;
      setForm({
        id: d.id,
        clientId: String(d.clientId),
        name: d.name || 'Weekly fleet movements',
        isEnabled: !!d.isEnabled,
        sendDayOfWeek: d.sendDayOfWeek ?? 1,
        sendTimeLocal: (d.sendTimeLocal || '07:00').slice(0, 5),
        includeAdded: d.includeAdded !== false,
        includeRemoved: d.includeRemoved !== false,
        includeTemp: d.includeTemp !== false,
        attachExcel: d.attachExcel !== false,
        sendIfEmpty: !!d.sendIfEmpty,
        userIds: (d.recipients || []).filter((r: any) => r.userId).map((r: any) => r.userId as number),
        ccEmails: (d.recipients || []).filter((r: any) => r.isOverride && r.email).map((r: any) => r.email).join(', '),
      });
      setPreview(null);
      setShowForm(true);
    } catch (e: any) {
      alert(e.response?.data?.message || 'Failed to load schedule.');
    }
  };

  const toggleUser = (id: number) => {
    setForm(prev => ({
      ...prev,
      userIds: prev.userIds.includes(id)
        ? prev.userIds.filter(x => x !== id)
        : [...prev.userIds, id],
    }));
  };

  const buildPayload = () => ({
    clientId: parseInt(form.clientId, 10),
    name: form.name,
    isEnabled: form.isEnabled,
    sendDayOfWeek: form.sendDayOfWeek,
    sendTimeLocal: form.sendTimeLocal,
    timeZoneId: 'Europe/London',
    includeAdded: form.includeAdded,
    includeRemoved: form.includeRemoved,
    includeTemp: form.includeTemp,
    attachExcel: form.attachExcel,
    sendIfEmpty: form.sendIfEmpty,
    userIds: form.userIds,
    ccEmails: form.ccEmails
      .split(/[,;\s]+/)
      .map(s => s.trim())
      .filter(Boolean),
  });

  const save = async () => {
    if (!form.clientId) {
      alert('Select a client.');
      return;
    }
    if (form.userIds.length === 0 && !form.ccEmails.trim()) {
      alert('Select at least one recipient.');
      return;
    }
    setSaving(true);
    try {
      const payload = buildPayload();
      if (form.id) await scheduledReportsAPI.update(form.id, payload);
      else await scheduledReportsAPI.create(payload);
      setShowForm(false);
      await loadAll();
    } catch (e: any) {
      alert(e.response?.data?.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id: number) => {
    if (!window.confirm('Delete this weekly schedule?')) return;
    try {
      await scheduledReportsAPI.remove(id);
      await loadAll();
    } catch (e: any) {
      alert(e.response?.data?.message || 'Delete failed.');
    }
  };

  const runNow = async (id: number) => {
    setBusyId(id);
    try {
      const res = await scheduledReportsAPI.runNow(id);
      alert(res.data?.message || 'Run complete.');
      await loadAll();
    } catch (e: any) {
      alert(e.response?.data?.message || 'Run failed.');
    } finally {
      setBusyId(null);
    }
  };

  const loadPreview = async () => {
    if (!form.clientId) {
      alert('Select a client first.');
      return;
    }
    try {
      const res = await scheduledReportsAPI.preview({
        clientId: parseInt(form.clientId, 10),
        includeAdded: form.includeAdded,
        includeRemoved: form.includeRemoved,
        includeTemp: form.includeTemp,
      });
      setPreview(res.data);
    } catch (e: any) {
      alert(e.response?.data?.message || 'Preview failed.');
    }
  };

  const testSend = async () => {
    if (!form.clientId) {
      alert('Select a client first.');
      return;
    }
    try {
      const res = await scheduledReportsAPI.testSend({
        clientId: parseInt(form.clientId, 10),
        includeAdded: form.includeAdded,
        includeRemoved: form.includeRemoved,
        includeTemp: form.includeTemp,
        attachExcel: form.attachExcel,
      });
      alert(res.data?.message || 'Test email sent to your admin inbox.');
    } catch (e: any) {
      alert(e.response?.data?.message || 'Test send failed.');
    }
  };

  return (
    <div>
      <div className="card" style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <div>
            <h3 style={{ margin: 0 }}>Weekly ons / offs schedules</h3>
            <p style={{ margin: '6px 0 0', color: '#6b7280', fontSize: 13 }}>
              Emails the full previous UK week (Mon 00:01 - Sun 23:59) to selected client users. Admin only.
            </p>
          </div>
          <button className="btn btn-primary" onClick={openCreate}>+ New schedule</button>
        </div>
      </div>

      {loading ? (
        <p>Loading...</p>
      ) : schedules.length === 0 ? (
        <div className="card">
          <p style={{ color: '#6b7280', fontStyle: 'italic', margin: 0 }}>No schedules yet. Create one for a client.</p>
        </div>
      ) : (
        <div className="card" style={{ overflowX: 'auto' }}>
          <table>
            <thead>
              <tr>
                <th>Client</th>
                <th>Send</th>
                <th>Recipients</th>
                <th>Last run</th>
                <th>Status</th>
                <th>Enabled</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {schedules.map(s => (
                <tr key={s.id}>
                  <td><strong>{s.clientName}</strong></td>
                  <td>{dayLabel(s.sendDayOfWeek)} {(s.sendTimeLocal || '').slice(0, 5)} UK</td>
                  <td>{s.recipientCount}</td>
                  <td>{s.lastRunAt ? formatDateBritish(s.lastRunAt) : '-'}</td>
                  <td>{statusBadge(s.lastRunStatus)}</td>
                  <td>{s.isEnabled ? 'Yes' : 'Paused'}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <button className="btn btn-secondary" onClick={() => openEdit(s)}>Edit</button>
                      <button className="btn btn-secondary" disabled={busyId === s.id} onClick={() => runNow(s.id)}>
                        {busyId === s.id ? 'Running...' : 'Run now'}
                      </button>
                      <button className="btn btn-secondary" onClick={() => remove(s.id)}>Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <div className="card" style={{ marginTop: 24 }}>
          <h3 style={{ marginTop: 0 }}>{form.id ? 'Edit schedule' : 'New schedule'}</h3>

          <div className="form-row">
            <div className="form-group">
              <label>Client</label>
              <select
                value={form.clientId}
                disabled={!!form.id}
                onChange={e => setForm(f => ({ ...f, clientId: e.target.value, userIds: [] }))}
              >
                <option value="">Select client...</option>
                {clients.map(c => (
                  <option key={c.id} value={c.id}>{c.companyName}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Name</label>
              <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
            </div>
            <div className="form-group">
              <label>Send day</label>
              <select
                value={form.sendDayOfWeek}
                onChange={e => setForm(f => ({ ...f, sendDayOfWeek: parseInt(e.target.value, 10) }))}
              >
                {DAY_OPTIONS.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Send time (UK)</label>
              <input
                type="time"
                value={form.sendTimeLocal}
                onChange={e => setForm(f => ({ ...f, sendTimeLocal: e.target.value }))}
              />
            </div>
          </div>

          <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', margin: '12px 0 18px' }}>
            <label><input type="checkbox" checked={form.isEnabled} onChange={e => setForm(f => ({ ...f, isEnabled: e.target.checked }))} /> Enabled</label>
            <label><input type="checkbox" checked={form.includeAdded} onChange={e => setForm(f => ({ ...f, includeAdded: e.target.checked }))} /> Include added</label>
            <label><input type="checkbox" checked={form.includeRemoved} onChange={e => setForm(f => ({ ...f, includeRemoved: e.target.checked }))} /> Include removed</label>
            <label><input type="checkbox" checked={form.includeTemp} onChange={e => setForm(f => ({ ...f, includeTemp: e.target.checked }))} /> Include temp</label>
            <label><input type="checkbox" checked={form.attachExcel} onChange={e => setForm(f => ({ ...f, attachExcel: e.target.checked }))} /> Attach CSV/Excel</label>
            <label><input type="checkbox" checked={form.sendIfEmpty} onChange={e => setForm(f => ({ ...f, sendIfEmpty: e.target.checked }))} /> Send if empty</label>
          </div>

          <div className="form-group">
            <label>Recipients (client portal users)</label>
            {!form.clientId ? (
              <p style={{ color: '#6b7280', fontSize: 13 }}>Select a client to load users.</p>
            ) : clientUsers.length === 0 ? (
              <p style={{ color: '#6b7280', fontSize: 13 }}>No active users found for this client.</p>
) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 10 }}>
                {clientUsers.map(u => {
                  const name = u.displayName || u.email || `User #${u.id}`;
                  const selected = form.userIds.includes(u.id);
                  return (
                    <div
                      key={u.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => toggleUser(u.id)}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleUser(u.id); } }}
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 10,
                        border: selected ? '1px solid #93c5fd' : '1px solid #e5e7eb',
                        borderRadius: 8,
                        padding: '10px 12px',
                        background: selected ? '#eff6ff' : '#fff',
                        cursor: 'pointer',
                        boxSizing: 'border-box',
                      }}
                    >
                      {/* width:auto required — global App.css sets input { width:100% } which crushed the text */}
                      <input
                        type="checkbox"
                        checked={selected}
                        onChange={() => toggleUser(u.id)}
                        onClick={(e) => e.stopPropagation()}
                        style={{
                          width: 'auto',
                          minWidth: 16,
                          height: 16,
                          marginTop: 3,
                          flex: '0 0 auto',
                          accentColor: '#1e40af',
                          cursor: 'pointer',
                        }}
                      />
                      <div style={{ flex: '1 1 auto', minWidth: 0 }}>
                        <div style={{
                          color: '#111827',
                          fontSize: 13,
                          fontWeight: 700,
                          letterSpacing: 'normal',
                          textTransform: 'none',
                          lineHeight: 1.3,
                          wordBreak: 'break-word',
                        }}>{name}</div>
                        <div style={{
                          color: '#6b7280',
                          fontSize: 12,
                          fontWeight: 400,
                          letterSpacing: 'normal',
                          textTransform: 'none',
                          marginTop: 2,
                          wordBreak: 'break-all',
                          lineHeight: 1.35,
                        }}>{u.email}</div>
                        <div style={{
                          display: 'inline-block',
                          marginTop: 6,
                          fontSize: 11,
                          fontWeight: 600,
                          letterSpacing: 'normal',
                          textTransform: 'none',
                          color: '#1e40af',
                          background: '#dbeafe',
                          borderRadius: 999,
                          padding: '2px 8px',
                        }}>{u.role}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="form-group" style={{ marginTop: 12 }}>
            <label>Extra CC emails (optional, comma-separated)</label>
            <input
              value={form.ccEmails}
              onChange={e => setForm(f => ({ ...f, ccEmails: e.target.value }))}
              placeholder="fleet@client.com, midterm@yourshield.co.uk"
            />
          </div>

          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 16 }}>
            <button className="btn btn-primary" disabled={saving} onClick={save}>{saving ? 'Saving...' : 'Save schedule'}</button>
            <button className="btn btn-secondary" onClick={loadPreview}>Preview last week</button>
            <button className="btn btn-secondary" onClick={testSend}>Test send to me</button>
            <button className="btn btn-secondary" onClick={() => setShowForm(false)}>Cancel</button>
          </div>

          {preview && (
            <div style={{ marginTop: 20, borderTop: '1px solid #e5e7eb', paddingTop: 16 }}>
              <h4 style={{ marginTop: 0 }}>Preview - {preview.clientName}</h4>
              <p style={{ margin: '0 0 8px', color: '#374151' }}>{preview.periodLabel}</p>
              <p style={{ margin: '0 0 12px', fontSize: 13, color: '#6b7280' }}>Subject: {preview.subject}</p>
              <div style={{ display: 'flex', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
                <div className="card" style={{ margin: 0, padding: '10px 14px' }}><strong style={{ color: '#16a34a' }}>+{preview.addedCount}</strong> added</div>
                <div className="card" style={{ margin: 0, padding: '10px 14px' }}><strong style={{ color: '#dc2626' }}>-{preview.removedCount}</strong> removed</div>
                <div className="card" style={{ margin: 0, padding: '10px 14px' }}><strong style={{ color: '#2563eb' }}>{preview.tempCount}</strong> temp</div>
              </div>
              {preview.totalCount === 0 ? (
                <p style={{ color: '#6b7280', fontStyle: 'italic' }}>No completed movements last week.</p>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table>
                    <thead>
                      <tr>
                        <th>Event</th>
                        <th>Registration</th>
                        <th>Site</th>
                        <th>Processed by</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.chronological.map((r, i) => (
                        <tr key={i}>
                          <td>{r.eventType}</td>
                          <td>{r.registration}</td>
                          <td>{r.site}</td>
                          <td>{r.processedBy}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ScheduledReportsTab;
